import { useState, useEffect } from 'react';
import { useAppContext } from '@/App';
import { getMyProgressDetailed } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle, Circle, Clock, Award, TrendingUp, BarChart3 } from 'lucide-react';

export default function MyProgress() {
  useAppContext(); // keeps the context warm for sibling pages
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        const res = await getMyProgressDetailed();
        if (!cancelled) {
          setData(res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
        <div className="h-64 bg-white rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data) return <p className="text-center py-16 text-[#062B1A]/50">No progress data available</p>;

  const kpis = [
    { label: 'Overall Progress', value: `${data.overall_progress}%`, icon: TrendingUp, accent: true },
    { label: 'Completed Lessons', value: `${data.total_completed}/${data.total_lessons}`, icon: CheckCircle, accent: false },
    { label: 'Average Score', value: `${data.average_score}%`, icon: Award, accent: true },
    { label: 'Study Time', value: `${Math.round(data.total_time_minutes / 60)}h ${data.total_time_minutes % 60}m`, icon: Clock, accent: false },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>My Progress</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">Track your learning journey across all courses</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, idx) => (
          <Card key={idx} className={`card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm ${kpi.accent ? 'gold-border-left' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{kpi.label}</span>
                <kpi.icon className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <p className="text-2xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overall Progress Bar */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Overall Learning Progress</h3>
            <span className="text-lg font-bold text-[#D4AF37]">{data.overall_progress}%</span>
          </div>
          <Progress value={data.overall_progress} className="h-3 bg-[#062B1A]/10 [&>div]:bg-gradient-to-r [&>div]:from-[#062B1A] [&>div]:to-[#D4AF37]" />
          <div className="flex items-center justify-between mt-2 text-xs text-[#062B1A]/40">
            <span>{data.total_completed} lessons completed</span>
            <span>{data.total_lessons} total lessons</span>
          </div>
        </CardContent>
      </Card>

      {/* Course-by-course Progress */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Progress Details</CardTitle>
        </CardHeader>
        <CardContent>
          {data.courses.length === 0 ? (
            <p className="text-sm text-[#062B1A]/50 text-center py-8">No enrolled courses</p>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {data.courses.map((course, idx) => (
                <AccordionItem key={course.course_id} value={course.course_id} className="border border-[rgba(6, 43, 26,0.08)] rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center gap-4 w-full pr-4">
                      <div className="w-10 h-10 rounded-lg bg-[#062B1A] flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-[#062B1A]">{course.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <Progress value={course.progress_pct} className="h-1.5 flex-1 max-w-[200px] bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]" />
                          <span className="text-xs text-[#062B1A]/50">{course.progress_pct}%</span>
                          <Badge variant="outline" className="text-[10px]">{course.completed_lessons}/{course.total_lessons} lessons</Badge>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <div className="space-y-4 pl-14">
                      {/* Lesson list */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40 mb-2">Lessons</p>
                        <div className="space-y-1">
                          {course.lessons.map((lesson) => (
                            <div key={lesson.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F7F8FA]">
                              {lesson.completed ? (
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <Circle className="w-4 h-4 text-[#062B1A]/20 flex-shrink-0" />
                              )}
                              <span className={`text-sm ${lesson.completed ? 'text-[#062B1A]' : 'text-[#062B1A]/60'}`}>{lesson.title}</span>
                              <Badge variant="outline" className="text-[10px] ml-auto">{lesson.type}</Badge>
                              <span className="text-[10px] text-[#062B1A]/40">{lesson.duration_minutes}m</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Exam submissions */}
                      {course.exam_submissions.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40 mb-2">Exam Results</p>
                          <div className="space-y-1">
                            {course.exam_submissions.map(sub => (
                              <div key={sub.id} className="flex items-center gap-3 p-2 rounded-lg bg-[#F7F8FA]">
                                <Award className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                                <span className="text-sm text-[#062B1A]">{sub.score}/{sub.max_score}</span>
                                <Badge className={`text-[10px] border-0 ${sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{sub.status}</Badge>
                                {sub.reference_id && (
                                  <button className="text-xs text-[#D4AF37] hover:underline ml-auto" onClick={() => navigate(`/student/exams/${sub.reference_id}/review`)}>Review</button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Assignment submissions */}
                      {course.assignment_submissions.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40 mb-2">Assignment Results</p>
                          <div className="space-y-1">
                            {course.assignment_submissions.map(sub => (
                              <div key={sub.id} className="flex items-center gap-3 p-2 rounded-lg bg-[#F7F8FA]">
                                <BarChart3 className="w-4 h-4 text-[#062B1A]/40 flex-shrink-0" />
                                <span className="text-sm text-[#062B1A]">{sub.score}/{sub.max_score}</span>
                                <Badge className={`text-[10px] border-0 ${sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{sub.status}</Badge>
                                {sub.feedback && <span className="text-xs text-[#062B1A]/50 ml-auto truncate max-w-[200px]">{sub.feedback}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
